import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/App";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Mail, Lock } from "lucide-react";

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/8f834926-4714-4959-b338-a287e83dcd5c/images/09115f449cc4321ff25180a33470ced8331fabee71ffcd319e7c00cf958ac54e.png";

export default function LoginPage() {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  // Forced password change state
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const navigate = useNavigate();
  const { login, user } = useAuth();

  if (user && !mustChangePassword) {
    navigate("/dashboard", { replace: true });
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isRegister ? "/auth/register" : "/auth/login";
      const data = isRegister ? { email, password, name } : { email, password };
      const res = await api.post(endpoint, data);
      if (res.data.must_change_password) {
        // Store session but don't navigate — show password change
        localStorage.setItem("session_token", res.data.session_token);
        login(res.data.user);
        setMustChangePassword(true);
        toast.info("Please set a new password before continuing");
      } else {
        login(res.data.user);
        toast.success(isRegister ? "Account created!" : "Welcome back!");
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (newPassword !== confirmPassword) { toast.error("Passwords do not match"); return; }
    setChangingPassword(true);
    try {
      await api.post("/auth/change-password", { new_password: newPassword });
      toast.success("Password changed! Welcome.");
      setMustChangePassword(false);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleGoogleAuth = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-8" data-testid="back-to-home-link">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <img src={LOGO_URL} alt="CashUpPOS" className="h-10 w-10" />
            <span className="font-heading text-2xl font-bold text-slate-900 tracking-tight">CashUpPOS</span>
          </div>
          <p className="text-sm text-slate-500 mt-2">
            {mustChangePassword ? "Set your new password to continue" : isRegister ? "Create your account to get started" : "Sign in to your account"}
          </p>
        </div>

        {/* Forced Password Change */}
        {mustChangePassword ? (
          <div className="bg-white border border-slate-200 rounded-lg p-8" data-testid="change-password-form">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="h-5 w-5 text-amber-500" />
              <h2 className="text-lg font-semibold text-slate-900">Change Your Password</h2>
            </div>
            <p className="text-sm text-slate-500 mb-6">Your account requires a password change before you can proceed.</p>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label htmlFor="newPassword" className="text-slate-700 text-sm">New Password</Label>
                <Input id="newPassword" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 6 characters" required className="mt-1" data-testid="new-password-input" />
              </div>
              <div>
                <Label htmlFor="confirmPassword" className="text-slate-700 text-sm">Confirm Password</Label>
                <Input id="confirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Retype your password" required className="mt-1" data-testid="confirm-password-input" />
              </div>
              <Button type="submit" disabled={changingPassword} className="w-full h-11 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="change-password-btn">
                {changingPassword ? "Changing..." : "Set New Password"}
              </Button>
            </form>
          </div>
        ) : (
        /* Regular Login/Register */
        <div className="bg-white border border-slate-200 rounded-lg p-8" data-testid="login-form-container">
          <Button
            variant="outline"
            className="w-full mb-6 h-11 border-slate-300 text-slate-700 hover:bg-slate-50"
            onClick={handleGoogleAuth}
            data-testid="google-auth-btn"
          >
            <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Continue with Google
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200" /></div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-slate-400">or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div>
                <Label htmlFor="name" className="text-slate-700 text-sm">Full Name</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="John Doe" required className="mt-1" data-testid="register-name-input" />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-slate-700 text-sm">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required className="mt-1" data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700 text-sm">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" required className="mt-1" data-testid="login-password-input" />
            </div>
            <Button
              type="submit" disabled={loading}
              className="w-full h-11 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white"
              data-testid="login-submit-btn"
            >
              <Mail className="mr-2 h-4 w-4" />
              {loading ? "Please wait..." : isRegister ? "Create Account" : "Sign In"}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            {isRegister ? "Already have an account?" : "Don't have an account?"}{" "}
            <button onClick={() => setIsRegister(!isRegister)} className="text-[var(--brand-primary)] font-medium hover:underline" data-testid="toggle-auth-mode-btn">
              {isRegister ? "Sign In" : "Create Account"}
            </button>
          </p>
          <p className="text-center text-xs text-slate-400 mt-2">
            New business? <a href="/register" className="text-[var(--brand-primary)] hover:underline" data-testid="register-business-link">Register your business</a>
          </p>
        </div>
        )}
      </div>
    </div>
  );
}
